import { atom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import { getMapDefaultConfig } from 'components/map';

type MapUserConfig = ReturnType<typeof getMapDefaultConfig>;

/** Entries of the map config that are settings, not layers in the Layers panel. */
const MAP_SETTINGS = new Set<keyof MapUserConfig>(['scrollZoom', 'popups']);

/**
 * The map defaults with every Layers panel layer off: the editor opens on just the
 * edited area and its reference areas, which are its own layers, and the user turns
 * on whatever else they want to see.
 *
 * @return {MapUserConfig} The config.
 */
const layersOffConfig = (): MapUserConfig => {
  const config = getMapDefaultConfig({ scrollZoom: { checked: true } });
  // Off as the Layers panel turns a layer off: the layer and each of its options,
  // which are what its layers draw from and what its checkbox reads.
  const next: MapUserConfig = { ...config };
  for (const key of Object.keys(config) as (keyof MapUserConfig)[]) {
    if (MAP_SETTINGS.has(key)) continue;
    const layer = config[key] as
      { checked: boolean; options?: Record<string, boolean> } | undefined;
    if (!layer) continue;
    const options = layer.options
      ? Object.fromEntries(
          Object.keys(layer.options).map((option) => [option, false])
        )
      : undefined;
    Object.assign(next, {
      [key]: options
        ? { ...layer, checked: false, options }
        : { ...layer, checked: false },
    });
  }
  return next;
};

/**
 * The editor map's own layer settings, so toggling a layer while editing leaves
 * the details map behind the modal as it was. One per editor session: each opening
 * starts with every layer off, whatever the last one turned on.
 *
 * @return {object} A fresh settings atom.
 */
export const createEditorMapConfigAtom = () => atom(layersOffConfig());

/** The editor map, for Zoom to area. */
export const editorMapRefAtom = atom<MapRef | null>(null);
